import React, { useEffect, useMemo, useState } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import AppLayout from '../components/AppLayout';
import { getProducts, updateProductSortPrice } from '../api/productApi';
import { formatDateTimeDDMMYYYY } from '../utils/dateFormat';
import styles from './ProductPriceList.module.css';

const translations = {
  en: {
    pageTitle: 'Product Price List',
    subtitle: 'Set the selling price for each product sort in the system.',
    searchPlaceholder: 'Search by name, SKU, or sort...',
    product: 'Product',
    sort: 'Sort',
    sku: 'SKU',
    size: 'Size',
    currentPrice: 'Current Price',
    newPrice: 'New Price',
    updatedBy: 'Updated By',
    updatedAt: 'Updated At',
    save: 'Save',
    reset: 'Reset',
    loading: 'Loading...',
    noProducts: 'No products match the current search.',
    failedFetch: 'Failed to load products. Please try again.',
    failedUpdate: 'Failed to update product price. Please try again.',
    updateSuccess: 'Sort price updated successfully.',
    homepage: 'Homepage',
    close: 'Close',
  },
  ar: {
    pageTitle: 'قائمة أسعار المنتجات',
    subtitle: 'حدد سعر البيع لكل صنف داخل المنتج.',
    searchPlaceholder: 'ابحث بالاسم أو رمز المنتج أو الصنف...',
    product: 'المنتج',
    sort: 'الصنف',
    sku: 'رمز المنتج',
    size: 'الحجم',
    currentPrice: 'السعر الحالي',
    newPrice: 'السعر الجديد',
    updatedBy: 'آخر تعديل بواسطة',
    updatedAt: 'تاريخ التعديل',
    save: 'حفظ',
    reset: 'إعادة',
    loading: 'جارٍ التحميل...',
    noProducts: 'لا توجد منتجات مطابقة للبحث الحالي.',
    failedFetch: 'فشل تحميل المنتجات. حاول مرة أخرى.',
    failedUpdate: 'فشل تحديث سعر المنتج. حاول مرة أخرى.',
    updateSuccess: 'تم تحديث سعر الصنف بنجاح.',
    homepage: 'الصفحة الرئيسية',
    close: 'إغلاق',
  },
};

const getSortKey = (productId, sortId) => `${productId}:${sortId}`;

const ProductPriceList = () => {
  const [language, setLanguage] = usePersistentLanguage();
  const [products, setProducts] = useState([]);
  const [priceDrafts, setPriceDrafts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const t = (key) => translations[language][key];

  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (!token) {
      return { userType: null };
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        userType: payload.userType,
        requesterUsername: payload.username,
      };
    } catch (error) {
      console.error('Error parsing token payload:', error);
      return { userType: null };
    }
  }, [token]);

  const formatPrice = (value) => {
    const numericValue = Number(value || 0);
    try {
      return new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
        style: 'currency',
        currency: 'EGP',
        minimumFractionDigits: 2,
      }).format(numericValue);
    } catch (error) {
      return `${numericValue.toFixed(2)} EGP`;
    }
  };

  const formatDate = (value) => {
    return formatDateTimeDDMMYYYY(value, '-');
  };

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const response = await getProducts();
        const productList = response.data || [];
        setProducts(productList);
        setPriceDrafts(
          productList.reduce((drafts, product) => {
            (product.sorts || []).forEach((sort) => {
              drafts[getSortKey(product._id, sort._id)] = String(sort.price ?? 0);
            });
            return drafts;
          }, {})
        );
      } catch (error) {
        console.error('Error loading products for pricing:', error);
        setErrorMessage(t('failedFetch'));
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const priceRows = useMemo(
    () =>
      products.flatMap((product) =>
        (product.sorts || []).map((sort) => ({
          product,
          sort,
          rowKey: getSortKey(product._id, sort._id),
        }))
      ),
    [products]
  );

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return priceRows;
    return priceRows.filter(({ product, sort }) =>
      [product.name, product.SKU, product.size, sort.sortName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [priceRows, searchTerm]);

  const handlePriceDraftChange = (productId, value) => {
    setPriceDrafts((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const resetPriceDraft = (product, sort) => {
    setPriceDrafts((prev) => ({
      ...prev,
      [getSortKey(product._id, sort._id)]: String(sort.price ?? 0),
    }));
  };

  const handleSavePrice = async (product, sort) => {
    const rowKey = getSortKey(product._id, sort._id);
    setErrorMessage(null);
    setSuccessMessage(null);
    setSavingId(rowKey);

    try {
      const response = await updateProductSortPrice(
        product._id,
        sort._id,
        priceDrafts[rowKey]
      );
      const updatedProduct = response.data;
      setProducts((prev) =>
        prev.map((item) => (item._id === updatedProduct._id ? updatedProduct : item))
      );
      setPriceDrafts((prev) => ({
        ...prev,
        ...((updatedProduct.sorts || []).reduce((drafts, updatedSort) => {
          drafts[getSortKey(updatedProduct._id, updatedSort._id)] = String(
            updatedSort.price ?? 0
          );
          return drafts;
        }, {})),
      }));
      setSuccessMessage(t('updateSuccess'));
    } catch (error) {
      console.error('Error updating product price:', error);
      setErrorMessage(error.response?.data?.details || t('failedUpdate'));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <AppLayout
      title={t('pageTitle')}
      subtitle={t('subtitle')}
      language={language}
      languageLabel={language === 'en' ? 'عربي' : 'English'}
      onToggleLanguage={() => setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'))}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.priceListPage}
      contentProps={{ 'aria-busy': loading || Boolean(savingId) }}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
          <p>{t('loading')}</p>
        </div>
      )}

      {!loading && (
        <>
          <section className={styles.toolbar}>
            <input
              type="search"
              className={styles.searchInput}
              placeholder={t('searchPlaceholder')}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </section>

          {errorMessage && (
            <div className={`${styles.alert} ${styles.alertError}`} role="alert">
              <span>{errorMessage}</span>
              <button type="button" onClick={() => setErrorMessage(null)}>
                {t('close')}
              </button>
            </div>
          )}

          {successMessage && (
            <div className={`${styles.alert} ${styles.alertSuccess}`} role="status">
              <span>{successMessage}</span>
              <button type="button" onClick={() => setSuccessMessage(null)}>
                {t('close')}
              </button>
            </div>
          )}

          <section className={styles.tableCard}>
            {filteredRows.length === 0 ? (
              <p className={styles.emptyState}>{t('noProducts')}</p>
            ) : (
              <table className={styles.priceTable}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('product')}</th>
                    <th>{t('sort')}</th>
                    <th>{t('sku')}</th>
                    <th>{t('size')}</th>
                    <th>{t('currentPrice')}</th>
                    <th>{t('newPrice')}</th>
                    <th>{t('updatedBy')}</th>
                    <th>{t('updatedAt')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map(({ product, sort, rowKey }, index) => {
                    const isSaving = savingId === rowKey;
                    const draftValue = priceDrafts[rowKey] ?? String(sort.price ?? 0);
                    return (
                      <tr key={rowKey}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{product.name}</strong>
                        </td>
                        <td>{sort.sortName}</td>
                        <td>{product.SKU}</td>
                        <td>{product.size}</td>
                        <td>{formatPrice(sort.price)}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className={styles.priceInput}
                            value={draftValue}
                            onChange={(event) =>
                              handlePriceDraftChange(rowKey, event.target.value)
                            }
                            disabled={isSaving}
                          />
                        </td>
                        <td>{sort.priceUpdatedBy || '-'}</td>
                        <td>{formatDate(sort.priceUpdatedAt)}</td>
                        <td>
                          <div className={styles.actionGroup}>
                            <button
                              type="button"
                              className={styles.saveButton}
                              onClick={() => handleSavePrice(product, sort)}
                              disabled={isSaving}
                            >
                              {t('save')}
                            </button>
                            <button
                              type="button"
                              className={styles.resetButton}
                              onClick={() => resetPriceDraft(product, sort)}
                              disabled={isSaving}
                            >
                              {t('reset')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </AppLayout>
  );
};

export default ProductPriceList;
